import React, { useEffect, useState, useCallback, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { Button } from "@mui/material";
import { getFAQbySection, updateFAQOrder } from "./Helper";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Swal from "sweetalert2";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

const ItemType = "FAQ";

const DraggableFAQItem = ({ faq, index, moveFAQ }) => {
  const ref = useRef(null);

  const [, drop] = useDrop({
    accept: ItemType,
    hover: (item, monitor) => {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      moveFAQ(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: ItemType,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  return (
    <div ref={ref} style={{ opacity: isDragging ? 0.5 : 1, cursor: "move" }}>
      <ListItem disablePadding>
        <ListItemIcon>
          <DragHandleIcon />
        </ListItemIcon>
        <Accordion className="w-full my-1" style={{ borderRadius: '0px' }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
            className="rounded-xl"
          >
            <h1 className="text-base font-semibold">{faq?.title}</h1>
          </AccordionSummary>
          <AccordionDetails>
            <h1 className="text-base">{faq?.description}</h1>
          </AccordionDetails>
        </Accordion>
      </ListItem>
    </div>
  );
};

const FAQSort = () => {
  const context = useOutletContext();
  const [selectedSection, setSelectedSection] = useState("General");
  const [faqs, setFAQs] = useState([]);

  useEffect(() => {
    getFAQbySection(selectedSection)
      .then((res) => setFAQs(res.data?.faq))
      .catch((err) => console.log(err));
  }, [selectedSection]);

  useEffect(() => {
    context(["FAQ", "Sort FAQs"]);
  }, [context]);

  const moveFAQ = useCallback((fromIndex, toIndex) => {
    setFAQs((prevFAQs) => {
      const updatedFAQs = [...prevFAQs];
      const [movedItem] = updatedFAQs.splice(fromIndex, 1);
      updatedFAQs.splice(toIndex, 0, movedItem);
      return updatedFAQs;
    });
  }, []);

  const submit = () => {
    const sorted_faqs = faqs.map((faq, index) => ({
      id: faq.id,
      title: faq.title,
      faq_no: index + 1,
    }));

    updateFAQOrder({ faqs: sorted_faqs })
      .then(() => Swal.fire("", "FAQs Order updated successfully", "success"))
      .catch((err) => {
        console.log(err);
        Swal.fire("Oops!", "Something went wrong", "error");
      });
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="p-5 shadow-md bg-white">
        <div className="grid grid-cols-6 gap-2">
          <h3
            className="col-span-5 text-lg uppercase tracking-wider font-bold"
            style={{ color: "#2EBAA3" }}
          >
            Sort FAQs
          </h3>
        </div>
        <br />
        <div className="overflow-x-auto relative">
          <br />
          <FormControl size="small" className="w-1/3">
            <InputLabel id="demo-simple-select-label">Section</InputLabel>
            <Select
              size="small"
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={selectedSection}
              label="Section"
              onChange={(e) => setSelectedSection(e.target.value)}
            >
              <MenuItem value="General">General</MenuItem>
              <MenuItem value="Client">Client</MenuItem>
              <MenuItem value="Vendor">Vendor</MenuItem>
              <MenuItem value="Consultant">Consultant</MenuItem>
            </Select>
          </FormControl>
          <br />
          <div className="p-5 overflow-y-scroll" style={{ height: "50vh" }}>
            <List>
              {faqs.map((faq, index) => (
                <DraggableFAQItem
                  key={faq.id}
                  faq={faq}
                  index={index}
                  moveFAQ={moveFAQ}
                />
              ))}
            </List>
          </div>
        </div>
        <div className="text-right pt-10">
          <Button onClick={submit} variant="contained" style={{ color: "white" }}>
            Save
          </Button>
        </div>
      </div>
    </DndProvider>
  );
};

export default FAQSort;
